import type { MouseEvent } from "react";
import { styled } from "styled-components";

import {
  Image,
  MarkdownArea,
  MasterDetailContentWrapper,
  UIActionButton,
  UIButtonsRow,
  UIChip,
} from "common/components";
import { breakpoints, lineTruncationStyle } from "common/styles";
import { hardNavigate } from "common/utilities/hardNavigate";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import type { TrackEvent } from "domains/analytics/types";
import { shareLinkAction } from "domains/share/actions";
import { getCanAccessDiscoverStakeholderProfile } from "entities/permissions/selectors";
import type { Stakeholder } from "entities/stakeholders/types";

const StyledTitle = styled.h2`
  font-size: 2rem;
  margin: 0 0 5px 0;
  ${lineTruncationStyle(1)};
`;

const StyledSubtitle = styled.p`
  margin: 0;
  font-size: 1.2rem;
  ${lineTruncationStyle(1)};
`;

const StyledInfo = styled.p`
  margin: 0;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.textFade};
`;

const StyledContent = styled.div`
  height: 100%;
  margin: 1rem 0;
  color: ${({ theme }) => theme.colors.textFade};

  @media ${breakpoints.medium} {
    margin: 2rem 0;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.textBright};

  @media ${breakpoints.medium} {
    flex-direction: row;
  }
`;

const StyledHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: 1rem;

  @media ${breakpoints.medium} {
    margin-top: 0;
    margin-left: 1rem;
  }
`;

const StyledTags = styled.div`
  margin-top: 10px;
`;

const StyledImage = styled(Image)`
  height: 200px;
  width: 100%;
  border-radius: 4px;

  @media ${breakpoints.medium} {
    height: 250px;
    width: 250px;
  }
`;

const StyledType = styled(UIChip)`
  font-size: 0.9rem;
  margin: 0;
  margin-right: 10px;

  &:last-child {
    margin-right: 0;
  }
`;

type StakeholderDetailProps = { shareLink: string; stakeholder?: Stakeholder };

export const StakeholderDetail = ({ stakeholder, shareLink }: StakeholderDetailProps) => {
  const dispatch = useDispatch();
  const canViewToplinkStakeholder = useSelector(getCanAccessDiscoverStakeholderProfile);
  const track = useTrack();
  const translate = useTranslate();

  if (!stakeholder) {
    return null;
  }

  const sendTrackEvent = (name: TrackEvent) =>
    track({ entityId: stakeholder.id, entityName: stakeholder.full_name, entityType: "Stakeholder", name });

  const onViewClick = (e: MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    sendTrackEvent("View Full Profile");
    if (stakeholder.profile_url !== null) hardNavigate(stakeholder.profile_url);
  };

  const onShareClick = () =>
    dispatch(
      shareLinkAction({
        id: stakeholder.id,
        name: stakeholder.full_name,
        link: `${import.meta.env.VITE_WEBSITE_URL}${shareLink}`,
        type: "Stakeholder",
      }),
    );

  return (
    <>
      <MasterDetailContentWrapper>
        <StyledHeader>
          <StyledImage src={stakeholder.images?.medium} alt={stakeholder.full_name} />
          <StyledHeaderContainer>
            <StyledInfo>{stakeholder.type}</StyledInfo>
            <StyledTitle>{stakeholder.full_name}</StyledTitle>
            <StyledSubtitle>{stakeholder.primary_position}</StyledSubtitle>
            <StyledInfo>{stakeholder.primary_organisation}</StyledInfo>
            <StyledTags>
              {(stakeholder.roles || []).map((role) => (
                <StyledType key={role.id} disabled>
                  {role.name}
                </StyledType>
              ))}
            </StyledTags>
            <UIButtonsRow>
              {canViewToplinkStakeholder && !!stakeholder.profile_url && (
                <UIActionButton
                  type="open"
                  onClick={onViewClick}
                  label={translate("app.discover.topic.tabs.stakeholders.view_profile")}
                />
              )}
              <UIActionButton type="share" onClick={onShareClick} />
            </UIButtonsRow>
          </StyledHeaderContainer>
        </StyledHeader>
        <StyledContent>
          <MarkdownArea>{stakeholder.bio}</MarkdownArea>
        </StyledContent>
      </MasterDetailContentWrapper>
    </>
  );
};
